<script setup lang="ts">
import BaseFilter from '@/components/base/BaseFilter.vue'
import BaseModal from '@/components/base/BaseModal.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import { useModal } from '@/composables/useModal'
import { useToast } from '@/composables/useToast'
import { useAuthStore } from '@/stores/auth'
import type { FilterOption } from '@/types/filter.types'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import SnsIcon from './SnsIcon.vue'

const isMobile = useIsMobile()
const authStore = useAuthStore()
const router = useRouter()
const toast = useToast()
const { isOpen, open, close } = useModal()
const editProfileOptions: FilterOption[] = [
  {
    idx: 0,
    name: '수정',
    data: 'edit',
  },
  {
    idx: 1,
    name: '삭제',
    data: 'delete',
  },
]

const menuOptions: FilterOption[] = [
  {
    idx: 0,
    name: '광고성 정보 수신',
    data: 'ads',
  },
  {
    idx: 1,
    name: '로그아웃',
    data: 'logout',
  },
  {
    idx: 2,
    name: '탈퇴하기',
    data: 'withdraw',
  },
]

const selectedMenuOption = ref<FilterOption>(menuOptions[0])

const handleMenuSelect = async (option: FilterOption) => {
  if (option.data === 'ads') {
    router.push('/my/ads')
  } else if (option.data === 'logout') {
    await authStore.logout()
    router.push('/')
  } else if (option.data === 'withdraw') {
    open()
  }
}

const handleWithdrawal = async () => {
  await authStore.withdrawal()
  router.push('/')
}

const selectedEditOption = ref<FilterOption>(editProfileOptions[0])

const handleEditSelect = async (option: FilterOption) => {
  if (option.data === 'edit') {
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = 'image/*'
    fileInput.onchange = async (e) => {
      const target = e.target as HTMLInputElement
      if (target.files && target.files.length > 0) {
        const selectedFile = target.files[0]
        try {
          await authStore.uploadProfileImage(selectedFile)
          toast.showToast('프로필 이미지 수정 성공', 'success')
        } catch (error) {
          console.error('프로필 이미지 수정 실패:', error)
          toast.showToast('프로필 이미지 수정 실패', 'error')
        }
      }
    }
    fileInput.click()
  } else if (option.data === 'delete') {
    try {
      await authStore.uploadProfileImage(null)
      toast.showToast('프로필 이미지 삭제 성공', 'success')
    } catch (error) {
      console.error('프로필 이미지 삭제 실패:', error)
      toast.showToast('프로필 이미지 삭제 실패', 'error')
    }
  }
}

const snsType = computed<'NAVER' | 'KAKAO' | 'GOOGLE'>(() => {
  return authStore.state.account?.socialType as 'NAVER' | 'KAKAO' | 'GOOGLE'
})
</script>

<template>
  <div class="my-view-account-info">
    <div class="my-view-account-info-title title-md white" :class="{ 'title-sm': isMobile }">
      계정정보
    </div>
    <div class="my-view-account-info-content">
      <div class="my-view-account-info-content-container">
        <div class="my-view-account-info-content-edit-button">
          <BaseFilter
            :options="editProfileOptions"
            :selectedOption="selectedEditOption"
            customTrigger
            @select="handleEditSelect"
            menuAlign="start"
            alwaysShowPcVersion
          >
            <template #trigger>
              <div class="profile-image-wrapper">
                <img
                  v-if="authStore.state.account?.profileUrl"
                  :src="authStore.state.account?.profileUrl"
                  class="profile-image"
                  alt="profile"
                />
                <div v-else class="profile-image-placeholder"></div>
                <div class="edit-icon-wrapper">
                  <img
                    class="svg-24 svg-white edit-icon"
                    src="@/assets/svgs/edit_pencil.svg"
                    alt="edit"
                  />
                </div>
              </div>
            </template>
          </BaseFilter>
        </div>
        <div class="my-view-account-info-content-name-level-container">
          <div class="my-view-account-info-content-name-row">
            <div class="my-view-account-info-content-name-row-name body-lg white">
              {{ authStore.state.account?.nickname }}
            </div>
            <SnsIcon :sns-type="snsType" />
          </div>
          <div
            v-show="authStore.state.account?.groupName"
            class="my-view-account-info-content-level body-sm gray-600"
          >
            그룹명 : {{ authStore.state.account?.groupName }}
          </div>
        </div>
        <div class="my-view-account-info-content-menu-container">
          <BaseFilter
            :options="menuOptions"
            :selectedOption="selectedMenuOption"
            customTrigger
            @select="handleMenuSelect"
            menuAlign="end"
            alwaysShowPcVersion
          >
            <template #trigger>
              <div class="my-view-account-info-content-menu">
                <img class="svg-24 svg-white" src="@/assets/svgs/more_horizontal.svg" alt="more" />
              </div>
            </template>
          </BaseFilter>
        </div>
      </div>
    </div>
  </div>

  <BaseModal
    :is-open="isOpen"
    title="프렌드를 탈퇴하실건가요?"
    confirmText="탈퇴하기"
    cancelText="취소"
    @close="close"
    @confirm="handleWithdrawal"
  >
    <p class="withdrawal-modal-text body-sm gray-200">
      프렌드를 탈퇴하시면 그동안 구매하신 내역들이<br />
      삭제되며, 복구할 수 없습니다.
    </p>
  </BaseModal>
</template>

<style scoped>
.my-view-account-info {
  padding: 0;
}

.my-view-account-info-content {
  margin-top: 16px;
}

.my-view-account-info-content-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  background-color: var(--gray-800);
  border-radius: 8px;
  padding: 20px;
}

.my-view-account-info-content-edit-button {
  width: 52px;
  height: 52px;
  border-radius: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-image-wrapper {
  width: 52px;
  height: 52px;
  position: relative;
  border-radius: 100%;
  overflow: hidden;
  cursor: pointer;
}

.profile-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-image-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(0deg, rgba(33, 33, 33, 0.5) 0%, rgba(33, 33, 33, 0.5) 100%), #8f98a8;
}

.edit-icon-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}

.edit-icon {
  width: 24px;
  height: 24px;
}

.profile-image-wrapper:hover .edit-icon-wrapper {
  opacity: 1;
}

.my-view-account-info-content-name-level-container {
  margin-left: 12px;
}

.my-view-account-info-content-name-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  gap: 4px;
}

.my-view-account-info-content-name-row-name {
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0px;
  max-width: 250px;
}

.my-view-account-info-content-level {
  margin-top: 4px;
}

.my-view-account-info-content-menu-container {
  margin-left: auto;
}

.profile-image {
  width: 52px;
  height: 52px;
  border-radius: 100%;
  object-fit: cover;
}

.profile-image-placeholder {
  width: 52px;
  height: 52px;
  border-radius: 100%;
  background: linear-gradient(0deg, rgba(33, 33, 33, 0.5) 0%, rgba(33, 33, 33, 0.5) 100%), #8f98a8;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.options-list) {
  top: 100%;
  left: 0;
  margin-top: 0;
}

.withdrawal-modal-text {
  text-align: start;
  margin-bottom: 28px;
}
</style>
